import ExpandLess from '@mui/icons-material/ExpandLess';
import ExpandMore from '@mui/icons-material/ExpandMore';
import { styled } from '@mui/material';
import Typography from '@mui/material/Typography';
import Column from 'components/Layout/Column';
import Row from 'components/Layout/Row';
import Avatar from 'further-ui/components/Avatar';
import Table, { type Column as TableColumn } from 'further-ui/components/Table';
import {
  numberToCurrencyString,
  numberToPercentageString,
  pluralize,
} from 'further-ui/utils';
import { useExpandedRows } from 'hooks/ui/useExpandedRows';
import { CommitmentData } from '../../types';

const PaddedRow = styled(Row)(({ theme }) => ({
  paddingLeft: theme.spacing(theme.spacingSizes.xxl),
}));

const StyledRow = styled(Row)(({ theme }) => ({
  padding: theme.spacing(theme.spacingSizes.xs, 0),
}));

type Props = Pick<CommitmentData, 'currencySettings'> & {
  holdingsByHolding: CommitmentData['realisedHoldings']['byHolding'];
};

const RealisedHoldingsByHolding: React.FC<Props> = ({
  currencySettings,
  holdingsByHolding: { data, totals },
}) => {
  const { currencyLcy } = currencySettings;
  const { toggleRowExpansion, expandedRows } = useExpandedRows();

  const columns: Array<TableColumn<(typeof data)[number]>> = [
    {
      label: 'Asset',
      key: 'assetName',
      paddingY: 0,
      width: 240,
      render: ({ assetImage, assetName, classes }) => (
        <StyledRow centered spacing="xs">
          <Avatar color="random" text={assetName} size="lg" src={assetImage} />
          <Column spacing="xxs">
            <Typography variant="inherit">{assetName}</Typography>
            <Typography variant="caption">
              {classes.length} {pluralize('class', classes.length, 'classes')}
            </Typography>
          </Column>
        </StyledRow>
      ),
      footer: (
        <>
          Total from {data.length} {pluralize('holding', data.length)}
        </>
      ),
    },
    {
      label: 'Realisation date',
      key: 'realisationDate',
      width: 140,
      render: ({ realisationDate }) =>
        realisationDate.toLabel(undefined, 'MMM dd, yyyy'),
    },
    {
      label: `Invested capital ${currencyLcy}`,
      key: 'investedCapitalLcy',
      width: 180,
      render: ({ investedCapitalLcy }) =>
        numberToCurrencyString(investedCapitalLcy, {
          currency: currencyLcy,
        }),
      footer: numberToCurrencyString(totals.investedCapitalLcy, {
        currency: currencyLcy,
      }),
    },
    {
      label: `Realisation value ${currencyLcy}`,
      key: 'realisationValueLcy',
      width: 180,
      render: ({ realisationValueLcy }) =>
        numberToCurrencyString(realisationValueLcy, {
          currency: currencyLcy,
        }),
    },
    {
      label: 'Gross MOIC',
      key: 'grossMoic',
      width: 120,
      render: ({ grossMoic }) => `${grossMoic}x`,
      footer: `${totals.grossMoic}x`,
    },
    {
      label: 'Gross IRR',
      key: 'grossIrr',
      width: 120,
      render: ({ grossIrr }) =>
        numberToPercentageString(grossIrr, { accuracy: 2 }),
      footer: numberToPercentageString(totals.grossIrr, {
        accuracy: 2,
      }),
    },
    {
      key: 'actions',
      width: 50,
      render: (row) =>
        expandedRows.has(row.id) ? (
          <ExpandLess fontSize="small" />
        ) : (
          <ExpandMore fontSize="small" />
        ),
    },
  ];

  const subRowColumns: Array<
    TableColumn<(typeof data)[number]['classes'][number]>
  > = [
    {
      key: columns[0].key,
      width: columns[0].width,
      render: ({ shareClassName }) => (
        <PaddedRow spacing="xs">{shareClassName}</PaddedRow>
      ),
    },
    {
      key: columns[1].key,
      width: columns[1].width,
      render: ({ investmentDate }) =>
        investmentDate.toLabel(undefined, 'MMM dd, yyyy'),
    },
    {
      key: columns[2].key,
      width: columns[2].width,
      render: ({ investedCapitalLcy }) =>
        numberToCurrencyString(investedCapitalLcy, {
          currency: currencyLcy,
        }),
    },
    {
      key: columns[3].key,
      width: columns[3].width,
      render: ({ realisationValueLcy }) =>
        numberToCurrencyString(realisationValueLcy, {
          currency: currencyLcy,
        }),
    },
    {
      key: columns[4].key,
      width: columns[4].width,
      render: ({ grossMoic }) => `${grossMoic}x`,
    },
    {
      key: columns[5].key,
      width: columns[5].width,
      render: ({ grossIrr }) =>
        numberToPercentageString(grossIrr, { accuracy: 2 }),
    },
    {
      key: columns[6].key,
      width: 50,
      render: () => null,
    },
  ];

  return (
    <Column spacing="md">
      <Table
        isExpandedRow={(row) => expandedRows.has(row.id)}
        getExpandedRows={(row) =>
          row.classes.map((holdingClass, rowIndex) => ({
            key: holdingClass.id,
            columns: subRowColumns.map((col) => ({
              key: col.key,
              content: col.render ? col.render(holdingClass, rowIndex) : null,
            })),
          }))
        }
        scrollable
        emptyMessage="No holdings"
        columns={columns}
        rows={data}
        onRowClick={(row) => toggleRowExpansion(row.id)}
      />
    </Column>
  );
};

export default RealisedHoldingsByHolding;
